import type { GetStaticPaths, GetStaticProps } from 'next';
import type { SingleConditionProps } from 'lib/queries/conditions';
import type { Blocks as BlocksType } from 'types/blocks';
import type { Author, Media } from 'types';
import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Head from 'next/head';
import { Avatar, Card, Link, Paragraph, Wrap } from '@leafwell/components';
import Loader from 'components/loader';
import PageWrap from 'components/page-wrap';
import HeadingBody from 'components/heading-body';
import WysiwygContent from 'components/wysiwyg-content';
import ConditionsByLetter from 'components/conditions-by-letter';
import MedicalCardHighlight from 'components/medical-card-highlight';
import StudyLegend from 'components/study-legend';
import Line from 'components/line';
import TableOfContentsBlock from 'components/table-contents-block';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { getSingleCondition } from 'lib/queries/conditions';
import { getLargestRemainderPercentages } from 'lib/utils/getLargestRemainderPercentages';
import { getDateDay, getDateMonth, getDateYear } from 'lib/utils/dateUtils';
import Blocks from 'components/blocks';
import MedicalConditionsJson from 'components/structured-data/medical-conditions';

const CONDITION_SECTIONS = [
  'overview',
  'research-overview',
  'do-i-qualify',
  'relevant-studies',
];

export type ConditionSections = { label: string; id: string }[];

type Role = 'reviewer' | 'writer' | 'editor';

const ROLE_TEXT: Record<Role, string> = {
  reviewer: 'Reviewed by',
  writer: 'Written by',
  editor: 'Edited by',
};

export function extractConditionName(title: string): string {
  // "Cannabis for [condition]"
  const cannabisFor = title.match(/^Cannabis for (.+)$/i);
  if (cannabisFor) {
    return capitalizeFirst(cannabisFor[1].trim());
  }

  // "Medical Marijuana for [condition]: ..."
  const medMarijuana = title.match(/^Medical Marijuana for ([^:]+)/i);
  if (medMarijuana) {
    return capitalizeFirst(medMarijuana[1].trim());
  }

  // "Cannabis and [condition]"
  const cannabisAnd = title.match(/^Cannabis and (.+)$/i);
  if (cannabisAnd) {
    return capitalizeFirst(cannabisAnd[1].trim());
  }

  // Default: return full title
  return title;
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const PersonLink: React.FC<{ person: Author }> = ({ person }) => (
  <Link className="text-lilac-500 hover:underline" href={person.uri}>
    {person.displayName || person.name}
  </Link>
);

const joinWithAnd = (nodes: React.ReactNode[]) => {
  // "A and B" or "A, B and C" (though here we’ll only ever have max 2 per role)
  return nodes.map((n, i) => {
    if (i === 0) return n;
    if (i === nodes.length - 1) return <> and {n}</>;
    return <>, {n}</>;
  });
};

export const PostContributors: React.FC<{
  postAuthor: Author[];
  reviewer?: Author | null;
  editedBy?: Author | null;
  displayEditor: boolean;
  displayContentReviewer: boolean;
  displayAuthor: boolean;
  createdAt?: string | null;
  modifiedAt?: string | null;
}> = ({
  postAuthor,
  reviewer,
  editedBy,
  displayEditor,
  displayContentReviewer,
  displayAuthor,
  createdAt,
  modifiedAt,
}) => {
  // 1) Build the candidate list honoring the display flags FIRST
  // Priority order of ROLES: reviewer > writer(s) > editor
  const candidates: { role: Role; person: Author }[] = [];

  if (displayContentReviewer && reviewer) {
    candidates.push({ role: 'reviewer', person: reviewer });
  }

  if (displayAuthor && postAuthor?.length) {
    for (const a of postAuthor) {
      candidates.push({ role: 'writer', person: a });
    }
  }

  if (displayEditor && editedBy) {
    candidates.push({ role: 'editor', person: editedBy });
  }

  // 2) Take up to TWO unique people following the above order
  const seen = new Set<string>();
  const selected: { role: Role; person: Author }[] = [];
  for (const c of candidates) {
    if (seen.has(c.person.id)) continue;
    selected.push(c);
    seen.add(c.person.id);
    if (selected.length === 2) break;
  }

  // 3) Collect avatars (one per selected person, in order)
  const profilePictures = selected
    .map(s => s.person.image || true)
    .filter(Boolean) as Media[];

  // 4) Build the label by grouping the selected people by role
  //    (e.g., "Written by A and B" OR "Reviewed by A | Written by B")
  const byRole: Record<Role, Author[]> = {
    reviewer: [],
    writer: [],
    editor: [],
  };
  for (const s of selected) byRole[s.role].push(s.person);

  const roleChunks: React.ReactNode[] = [];

  (['reviewer', 'writer', 'editor'] as Role[]).forEach(role => {
    if (byRole[role].length) {
      const links = byRole[role].map(p => (
        <PersonLink key={`${role}-${p.id}`} person={p} />
      ));
      roleChunks.push(
        <React.Fragment key={role}>
          {ROLE_TEXT[role]} {joinWithAnd(links)}
        </React.Fragment>,
      );
    }
  });

  const label = roleChunks.reduce<React.ReactNode[]>((acc, chunk, i) => {
    acc.push(chunk);
    if (i < roleChunks.length) acc.push(' | ');
    if (i === roleChunks.length - 1)
      acc.push(
        createdAt === modifiedAt
          ? `Published on ${createdAt}`
          : `Last updated on ${modifiedAt}`,
      );
    return acc;
  }, []);

  return (
    <div className=" flex flex-col md:flex-row items-start md:items-center gap-4">
      <div className="flex -space-x-2">
        {profilePictures.map((img, i) => (
          <Avatar
            size="large"
            image={img?.src}
            width="48"
            height="48"
            Component={Image}
            className="rounded-full ring-2 ring-white object-cover"
          />
        ))}
      </div>

      <div className="text-base text-gray-700">
        <div className="mb-1">{label}</div>
      </div>
    </div>
  );
};

const OverviewSection: React.FC<{
  id: string;
  date: string;
  modified: string;
  conditionTitle: string;
  content: string;
  author?: Author;
  contentReviewer?: Author;
  editedBy?: Author;
  displayContentReviewer?: boolean;
  displayAuthor?: boolean;
  displayEditor?: boolean;
  readTime?: {
    words: number;
    minutes: number;
    seconds: number;
    wpm: number;
  };
  blocks?: BlocksType[];
  keyTakeaways?: string;
}> = ({
  id,
  date,
  modified,
  conditionTitle,
  content,
  author,
  contentReviewer,
  editedBy,
  displayContentReviewer,
  displayAuthor,
  displayEditor,
  readTime,
  blocks,
  keyTakeaways,
}) => {
  const createdAt = `${getDateMonth(date)} ${getDateDay(date)}, ${getDateYear(
    date,
  )}`;
  const modifiedAt = `${getDateMonth(modified)} ${getDateDay(
    modified,
  )}, ${getDateYear(modified)}`;

  const hasAuthor = author || contentReviewer || editedBy;

  return (
    <section
      key={id}
      id={id}
      className="grid md:grid-cols-condition-details gap-y-6 gap-x-24"
    >
      <HeadingBody
        level="1"
        className="flex-1 font-semibold"
        title={conditionTitle}
      />
      {hasAuthor ? (
        <PostContributors
          postAuthor={[author]}
          reviewer={contentReviewer}
          editedBy={editedBy}
          displayEditor={displayEditor}
          displayContentReviewer={displayContentReviewer}
          displayAuthor={displayAuthor}
          createdAt={createdAt}
          modifiedAt={modifiedAt}
        />
      ) : null}
      <div className="grid grid-cols-auto-1fr gap-x-2">
        <Image
          src="/education-icon.svg"
          alt="book-icon"
          className="mt-1"
          loading="lazy"
          width={18}
          height={18}
        />
        <p className="text-xs">{`${readTime?.minutes} min read`}</p>
      </div>
      {keyTakeaways ? (
        <WysiwygContent
          className="rounded-lg border border-gray-100 p-4 shadow-lg shadow-lilac-500/20 mb-6 max-w-3xl mx-auto"
          content={keyTakeaways}
          tagName="section"
        />
      ) : null}
      <WysiwygContent content={content} />
      {blocks?.length > 0 ? <Blocks blocks={blocks} /> : null}
    </section>
  );
};

const ResearchOverviewSection: React.FC<
  SingleConditionProps['conditionStudies'] & {
    id?: string;
    conditionTitle?: string;
    researchOverviewTitle?: string;
  }
> = ({
  id,
  studies,
  resultsDetails,
  typesDetails,
  conditionTitle,
  researchOverviewTitle,
}) => {
  const { translations } = useWordPressContext();

  const totalStudies = studies.pagination.aggregate.count;

  const percentages = useMemo(
    () =>
      getLargestRemainderPercentages(resultsDetails.map(({ count }) => count)),
    [resultsDetails],
  );
  const getPercentage = useCallback(
    key => percentages.find(({ key: itemKey }) => key === itemKey)?.value || 0,
    [percentages],
  );

  const STUDY_TYPES_OVERVIEW = useMemo(
    () => [
      ...typesDetails,
      {
        type: translations['Total studies'],
        count: totalStudies,
      },
    ],
    [typesDetails, totalStudies],
  );

  return (
    <section key={id} id={id}>
      <Line className="mb-9" />
      <HeadingBody
        className="!mb-8 font-semibold"
        level="2"
        title={researchOverviewTitle}
      />
      <div className="grid md:grid-cols-4 gap-x-10 md:gap-y-8 items-end mb-8 md:mb-24">
        {STUDY_TYPES_OVERVIEW.map(({ type, count }, idx) => {
          const isLast = idx === STUDY_TYPES_OVERVIEW.length - 1;

          return (
            <div
              key={idx}
              className={[
                'flex-col mb-16 md:mb-0',
                isLast ? 'md:col-span-2' : '',
              ].join(' ')}
            >
              <Card
                size="medium"
                filled={true}
                rounded={true}
                variant="light"
                tagName="article"
                className="relative h-48"
              >
                {type && <Paragraph>{translations[type] || type}</Paragraph>}
                {isLast ? (
                  <HeadingBody
                    level="4"
                    title={conditionTitle}
                    className="absolute top-12 left-6"
                  />
                ) : null}
                <HeadingBody
                  level="3"
                  title={count?.toString()}
                  className="self-end"
                />
              </Card>
            </div>
          );
        })}
      </div>
      <section>
        {resultsDetails.map(({ count, result }, idx) => {
          const percentage = getPercentage(idx);

          return (
            <div key={idx} className="grid grid-cols-3 mb-12">
              <HeadingBody level="4" title={result} className="font-sans" />
              <Paragraph
                size="small"
                className="text-center"
              >{`${count} ${translations['studies']}`}</Paragraph>
              <HeadingBody
                level="3"
                title={`${percentage}%`}
                className="text-right"
              />
              <div
                className="col-span-full border-t-[3px] border-green"
                style={{
                  width: percentage === 0 ? '0.5%' : `${percentage}%`,
                }}
              ></div>
            </div>
          );
        })}
      </section>
    </section>
  );
};

const DoIQualifySection: React.FC<
  Pick<SingleConditionProps['post']['acf'], 'cta'> & {
    id?: string;
  }
> = ({ cta, id }) => {
  return (
    <section key={id} id={id}>
      <MedicalCardHighlight {...cta} wrap={false} />
    </section>
  );
};

const RelevantStudies: React.FC<
  Pick<SingleConditionProps['conditionStudies'], 'studies'> & {
    id?: string;
    studiesTitle?: string;
    studiesDescription?: string;
  }
> = ({ studies, id, studiesTitle, studiesDescription }) => (
  <section
    id={id}
    className="mt-8 md:mt-28 grid md:grid-cols-[40%_auto] lg:grid-cols-condition-details md:gap-x-16 lg:gap-x-40"
  >
    <div className="mb-16">
      <HeadingBody
        className="!mb-8 font-semibold"
        level="2"
        title={studiesTitle}
      />
      <Paragraph dangerouslySetInnerHTML={{ __html: studiesDescription }} />
    </div>
    <ul>
      {studies.nodes.map(({ title, link, typeofstudy, result }, idx) => (
        <li
          key={`study-${idx}`}
          className="mb-16 md:mb-4 rounded-lg md:p-8 hover:bg-lilac-50"
        >
          <a
            className="underline underline-offset-2 hover:cursor-pointer"
            href={link}
            target="_blank"
          >
            {title}
            <Image
              className="inline-block ml-2"
              src="/external-link-icon.svg"
              height={15}
              width={15}
              alt=""
            />
          </a>
          <StudyLegend
            type={typeofstudy}
            result={result}
            className="mt-5 flex-col lg:flex-row"
          />
        </li>
      ))}
    </ul>
  </section>
);

const ConditionContent: React.FC<
  Pick<
    SingleConditionProps['post'],
    'date' | 'modified' | 'title' | 'content'
  > &
    Pick<
      SingleConditionProps['post']['acf'],
      | 'cta'
      | 'studiesTitle'
      | 'studiesDescription'
      | 'researchOverviewTitle'
      | 'author'
      | 'contentReviewer'
      | 'editedBy'
      | 'displayContentReviewer'
      | 'displayAuthor'
      | 'displayEditor'
      | 'blocks'
      | 'keyTakeaways'
    > &
    SingleConditionProps['conditionStudies'] & {
      sections: ConditionSections;
      readTime?: {
        words: number;
        minutes: number;
        seconds: number;
        wpm: number;
      };
    }
> = ({
  date,
  modified,
  title: conditionTitle,
  content,
  sections,
  cta,
  researchOverviewTitle,
  studiesTitle,
  studiesDescription,
  studies,
  resultsDetails,
  typesDetails,
  author,
  contentReviewer,
  editedBy,
  displayContentReviewer,
  displayAuthor,
  displayEditor,
  readTime,
  blocks,
  keyTakeaways,
}) => {
  return (
    <Wrap className="!pt-0">
      {sections.map(({ id }) => {
        switch (id) {
          case CONDITION_SECTIONS[0]:
            return (
              <OverviewSection
                key={id}
                id={id}
                date={date}
                modified={modified}
                conditionTitle={conditionTitle}
                content={content}
                author={author}
                contentReviewer={contentReviewer}
                editedBy={editedBy}
                displayContentReviewer={displayContentReviewer}
                displayAuthor={displayAuthor}
                displayEditor={displayEditor}
                readTime={readTime}
                blocks={blocks}
                keyTakeaways={keyTakeaways}
              />
            );
          case CONDITION_SECTIONS[1]:
            return (
              <ResearchOverviewSection
                key={id}
                id={id}
                studies={studies}
                resultsDetails={resultsDetails}
                typesDetails={typesDetails}
                conditionTitle={conditionTitle}
                researchOverviewTitle={researchOverviewTitle}
              />
            );
          case CONDITION_SECTIONS[2]:
            return <DoIQualifySection key={id} id={id} cta={cta} />;
          case CONDITION_SECTIONS[3]:
            return (
              <RelevantStudies
                key={id}
                id={id}
                studies={studies}
                studiesTitle={studiesTitle}
                studiesDescription={studiesDescription}
              />
            );
        }
      })}
    </Wrap>
  );
};

const SinglePostContent: React.FC<SingleConditionProps> = ({
  post: {
    date,
    modified,
    title,
    content,
    seo,
    acf: {
      cta,
      studiesTitle,
      studiesDescription,
      researchOverviewTitle,
      author,
      contentReviewer,
      editedBy,
      displayContentReviewer,
      displayAuthor,
      displayEditor,
      blocks,
      keyTakeaways,
      alternateNames,
      medicalSpecialty,
      wysiwygTemplate,
    },
  },
  tableOfContents,
  conditionsByLetter: { conditions, pagination, letter },
  conditionStudies: { studies, resultsDetails, typesDetails },
  readTime,
}) => {
  const { translations } = useWordPressContext();

  const sections = useMemo(() => {
    const filteredSections = [];
    CONDITION_SECTIONS.map(id => {
      switch (id) {
        case 'overview':
          if (content) {
            filteredSections.push({
              id,
              label: translations[id],
            });
          }
          break;
        case 'research-overview':
        case 'relevant-studies':
          if (studies?.nodes?.length > 0) {
            filteredSections.push({
              id,
              label: translations[id],
            });
          }
          break;
        default:
          filteredSections.push({
            id,
            label: translations[id],
          });
      }
    });
    return filteredSections;
  }, [translations, content, studies]);

  return wysiwygTemplate && wysiwygTemplate.length > 0 ? (
    <PageWrap seo={seo} title={title}>
      <Wrap
        size="large"
        className="items-center justify-center !pt-0 xl:-mt-10"
      >
        <WysiwygContent {...{ content: wysiwygTemplate }} />
      </Wrap>
    </PageWrap>
  ) : (
    <PageWrap seo={seo} title={title} pageType={null}>
      <MedicalConditionsJson
        title={title}
        description={seo.description}
        url={seo.canonical}
        name={extractConditionName(title)}
        datePublished={date}
        author={author?.displayName || 'Dipak Hemraj'}
        authorJobTitle={author?.jobTitles[0] || 'Medical Cannabis Researcher'}
        reviewedBy={contentReviewer?.displayName || 'Gavin Moreland, MD'}
        reviewerJobTitle={contentReviewer?.jobTitles[0] || 'Medical Director'}
        lastReviewed={modified}
        alternateName={
          alternateNames && alternateNames.length > 0
            ? alternateNames?.map(({ alternateName }) => alternateName)
            : []
        }
        specialty={medicalSpecialty || ''}
      />
      <ConditionContent
        date={date}
        modified={modified}
        title={title}
        sections={sections}
        content={content}
        studies={studies}
        cta={cta}
        studiesTitle={studiesTitle}
        studiesDescription={studiesDescription}
        resultsDetails={resultsDetails}
        typesDetails={typesDetails}
        researchOverviewTitle={researchOverviewTitle}
        author={author}
        contentReviewer={contentReviewer}
        editedBy={editedBy}
        displayContentReviewer={displayContentReviewer}
        displayAuthor={displayAuthor}
        displayEditor={displayEditor}
        readTime={readTime}
        blocks={blocks}
        keyTakeaways={keyTakeaways}
      />
      <TableOfContentsBlock
        tableOfContents={tableOfContents}
        acfFcLayout="table_of_contents"
      />
      <div className="relative before:-z-1 before:pointer-events-none before:bg-gray-100 before:absolute before:bottom-0 before:left-0 before:w-screen before:h-full">
        <ConditionsByLetter
          letter={letter}
          variant="minimal"
          activeCondition={title}
          conditions={conditions}
          pagination={pagination}
        />
      </div>
    </PageWrap>
  );
};

const SingleCondition: React.FC<SingleConditionProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <SinglePostContent {...props} />;
};

export const getStaticProps: GetStaticProps = async context => {
  return await getSingleCondition(context);
};

export const getStaticPaths: GetStaticPaths = async context => {
  return { paths: [], fallback: 'blocking' };
};

export default SingleCondition;
